import { apiGet, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseFinancialFundamental {
  private container: HTMLElement;
  private config: FinancialConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private period?: 'Yearly' | 'Quarterly';
  private selectedRecordIndex = 0;
  private selectedStatementType: 'Income' | 'BalanceSheet' | 'CashflowStatement' | 'LatestFinancialStatements' | 'IncomeStatmentTtm' | 'BalanceSheetStatmentsTtm' | 'CashflowStatementsTtm' = 'Income';
  private selectedMetrics: string[] = [];
  private reportInterval: 'Annual' | 'Quarterly' = 'Annual';

  constructor(config: FinancialConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<FinancialConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      type: "Cash and Debt",
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      period: 'Yearly'
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}


    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()}Fundamentals</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
              </div>
              <span id="symbol-dropdown-placeholder"></span>
            </div>`}
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <select class="filter-dropdown report-interval-select">
                  <option value="Annual" selected>Annual</option>
                  <option value="Quarterly">Quarterly</option>
                </select>
              </div>
              <div class="item">
                <select class="filter-dropdown statement-type-select">
                  <option value="Income" selected>Income</option>
                  <option value="BalanceSheet">Balance Sheet</option>
                  <option value="CashflowStatement">Cashflow Statement</option>
                  <option value="LatestFinancialStatements">Latest Financial Statements</option>
                  <option value="IncomeStatmentTtm">Income Statment ttm</option>
                  <option value="BalanceSheetStatmentsTtm">Balance Sheet Statments Ttm</option>
                  <option value="CashflowStatementsTtm">Cashflow Statements Ttm</option>
                </select>
              </div>
              <div class="item">
                <input type="text" class="metric-search" placeholder="Search metrics..." style="width: 100%; margin-bottom: 8px;" />
              </div>
              <div class="item checkBoxes checkBoxes02" style="flex: 0 0 100%;">
              </div>
              <div class="item">
                <button class="return-to-default-btn" type="button" style="width: 100%; min-width: 0;">Return to default</button>
              </div>
            </div>
          </div>
          <div class="chartDv" id="cashDebtContainer"></div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) placeholder.replaceWith(dropdown);
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
        const metricSearch = this.shadowRoot.querySelector('.metric-search') as HTMLInputElement;
        if (metricSearch) {
          metricSearch.addEventListener('input', () => {
            this.renderSelectedRecord();
            this.updateReturnToDefaultButton();
          });
        }
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    symbolDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;

    const recordDropdown = this.shadowRoot.querySelector('.record-dropdown') as HTMLSelectElement;
    recordDropdown?.addEventListener('change', (e) => {
      const value = Number((e.target as HTMLSelectElement).value);
      if (!Number.isNaN(value)) {
        this.selectedRecordIndex = value;
        this.renderSelectedRecord();
      }
    });

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', () => {
        filtersBox.classList.toggle('hidden');
      });
    }

    const statementTypeSelect = this.shadowRoot.querySelector('.statement-type-select') as HTMLSelectElement;
    if (statementTypeSelect) {
      statementTypeSelect.addEventListener('change', (e) => {
        const value = (e.target as HTMLSelectElement).value as 'Income' | 'BalanceSheet' | 'CashflowStatement' | 'LatestFinancialStatements' | 'IncomeStatmentTtm' | 'BalanceSheetStatmentsTtm' | 'CashflowStatementsTtm';
        this.selectedStatementType = value;
        this.selectedRecordIndex = 0;
        this.selectedMetrics = [];
        this.setupChart(this.config.symbol!);
        this.updateReturnToDefaultButton();
      });
    }

    const reportIntervalSelect = this.shadowRoot.querySelector('.report-interval-select') as HTMLSelectElement;
    if (reportIntervalSelect) {
      reportIntervalSelect.value = this.reportInterval;
      reportIntervalSelect.addEventListener('change', (e) => {
        const value = (e.target as HTMLSelectElement).value as 'Annual' | 'Quarterly';
        this.reportInterval = value;
        this.selectedRecordIndex = 0;
        this.setupChart(this.config.symbol!);
        this.updateReturnToDefaultButton();
      });
    }

    const returnToDefaultBtn = this.shadowRoot.querySelector('.return-to-default-btn') as HTMLButtonElement;
    returnToDefaultBtn?.addEventListener('click', () => this.resetFiltersToDefault());
    this.updateReturnToDefaultButton();
  }

  private isFiltersAtDefault(): boolean {
    const metricSearch = this.shadowRoot.querySelector('.metric-search') as HTMLInputElement | null;
    const searchEmpty = !metricSearch?.value.trim();
    if (this.reportInterval !== 'Annual') return false;
    if (this.selectedStatementType !== 'Income') return false;
    if (!searchEmpty) return false;
    const record = Array.isArray(this.currentData) ? this.currentData[0] : null;
    if (!record) return true;
    const defaults = this.pickDefaultMetricKeys(this.getNumericFields(record));
    if (this.selectedMetrics.length !== defaults.length) return false;
    return defaults.every((key) => this.selectedMetrics.includes(key));
  }

  private updateReturnToDefaultButton(): void {
    const btn = this.shadowRoot.querySelector('.return-to-default-btn') as HTMLButtonElement | null;
    if (!btn) return;
    const atDefault = this.isFiltersAtDefault();
    btn.disabled = atDefault;
    btn.style.opacity = atDefault ? '0.5' : '1';
    btn.style.cursor = atDefault ? 'default' : 'pointer';
  }

  private resetFiltersToDefault(): void {
    this.reportInterval = 'Annual';
    this.selectedStatementType = 'Income';
    this.selectedMetrics = [];
    this.selectedRecordIndex = 0;

    const reportIntervalSelect = this.shadowRoot.querySelector('.report-interval-select') as HTMLSelectElement | null;
    if (reportIntervalSelect) reportIntervalSelect.value = 'Annual';

    const statementTypeSelect = this.shadowRoot.querySelector('.statement-type-select') as HTMLSelectElement | null;
    if (statementTypeSelect) statementTypeSelect.value = 'Income';

    const metricSearch = this.shadowRoot.querySelector('.metric-search') as HTMLInputElement | null;
    if (metricSearch) metricSearch.value = '';

    this.setupChart(this.config.symbol!);
    this.updateReturnToDefaultButton();
  }

  private formatApiDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  /** Accepts raw array or wrapped `{ data: [...] }` (and similar) from the FMP proxy. */
  private parseStatementApiPayload(json: unknown): any[] {
    if (Array.isArray(json)) return json;
    if (json && typeof json === 'object' && 'data' in json) {
      const d = (json as { data: unknown }).data;
      if (Array.isArray(d)) return d;
    }
    return [];
  }

  private getStatementDateQuery(): { startDate: string; endDate: string; intervals: string } {
    const end = new Date();
    const start = new Date(end);
    if (this.reportInterval === 'Annual') {
      start.setFullYear(start.getFullYear() - 10);
    } else {
      start.setFullYear(start.getFullYear() - 3);
    }
    return {
      startDate: this.formatApiDate(start),
      endDate: this.formatApiDate(end),
      intervals: this.reportInterval === 'Annual' ? 'annual' : 'quarter'
    };
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const { startDate, endDate, intervals } = this.getStatementDateQuery();
      const statementQs = `symbol=${symbol}&startDate=${startDate}&endDate=${endDate}&intervals=${intervals}&columnName=&sortOrder=asc&limit=&offset=0`;
      let apiUrl: string;
      switch (this.selectedStatementType) {
        case 'BalanceSheet':
          apiUrl = `${APIURL}/v1/fmp/balance-sheet?${statementQs}`;
          break;
        case 'BalanceSheetStatmentsTtm':
          apiUrl = `${APIURL}/v1/financial/balance-sheet-ttm?symbol=${symbol}`;
          break;
        case 'CashflowStatement':
          apiUrl = `${APIURL}/v1/fmp/cash-flow?${statementQs}`;
          break;
        case 'CashflowStatementsTtm':
          apiUrl = `${APIURL}/v1/financial/cash-flow-ttm?symbol=${symbol}`;
          break;
        case 'IncomeStatmentTtm':
          apiUrl = `${APIURL}/v1/financial/income-statement-ttm?symbol=${symbol}`;
          break;
        case 'LatestFinancialStatements':
          apiUrl = `${APIURL}/v1/fmp/financial-ratios?symbol=${symbol}&period=${intervals}&limit=&offset='0'`;
          break;
        case 'Income':
        default:
          apiUrl = `${APIURL}/v1/fmp/income-statement?${statementQs}`;
          break;
      }

      const res = await apiGet({
        url: apiUrl,
        method: "GET",
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const json = await res.json();
      this.currentData = this.parseStatementApiPayload(json);
      this.currentData.sort((a: any, b: any) => {
        const ta = new Date(a?.date ?? a?.filingDate ?? 0).getTime();
        const tb = new Date(b?.date ?? b?.filingDate ?? 0).getTime();
        return tb - ta;
      });
      this.selectedRecordIndex = 0;
      this.populateRecordDropdown();

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private updateDisplay(): void {
    if (!Array.isArray(this.currentData) || !(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }

    this.populateRecordDropdown();
    this.renderSelectedRecord();
  }

  private renderSelectedRecord(): void {
    const container = this.shadowRoot.querySelector('#cashDebtContainer');
    if (!container) return;

    const html = `<div class="cashDebtMain"><div id="chartContainer" class="chart"></div></div>`;
    container.innerHTML = html;

        const record = Array.isArray(this.currentData) ? this.currentData[this.selectedRecordIndex] : null;
        const metricBox = this.shadowRoot.querySelector('.checkBoxes');
        if (metricBox && record) {
          const allMetrics = this.getNumericFields(record);
          let filteredMetrics = allMetrics;
          const metricSearch = this.shadowRoot.querySelector('.metric-search') as HTMLInputElement;
          if (metricSearch && metricSearch.value.trim()) {
            const searchTerm = metricSearch.value.trim().toLowerCase();
            filteredMetrics = allMetrics.filter(m => m.label.toLowerCase().includes(searchTerm));
          }
          const selectedMetricObjs = this.selectedMetrics
            .map(key => allMetrics.find(m => m.key === key))
            .filter(Boolean) as { key: string; label: string }[];
          const displayMetrics: { key: string; label: string }[] = [];
          const seen = new Set<string>();
          for (const m of [...selectedMetricObjs, ...filteredMetrics]) {
            if (!seen.has(m.key)) {
              displayMetrics.push(m);
              seen.add(m.key);
            }
          }
          if (!this.selectedMetrics || this.selectedMetrics.length === 0 || !this.selectedMetrics.every(key => allMetrics.some(m => m.key === key))) {
            this.selectedMetrics = this.pickDefaultMetricKeys(allMetrics);
          }
          metricBox.innerHTML = displayMetrics.map((m, i) => {
            const checked = this.selectedMetrics.includes(m.key);
            const disable = this.selectedMetrics.length >= 4 && !checked;
            return `<div class="checkBoxField${disable ? ' disabled' : ''}"><input class="filter-checkbox" type="checkbox" value="${m.key}" id="metric-${i}" ${checked ? 'checked' : ''} ${disable ? 'disabled' : ''}/><label for="metric-${i}">${m.label} <i>✓</i></label></div>`;
          }).join('');
          const metricCheckboxEls = metricBox.querySelectorAll('.filter-checkbox') as NodeListOf<HTMLInputElement>;
          metricCheckboxEls.forEach(cb => {
            cb.addEventListener('change', () => {
              const checkedMetrics = Array.from(metricBox.querySelectorAll('.filter-checkbox:checked')).map((c:any) => c.value);
              if (checkedMetrics.length > 4) {
                cb.checked = false;
                return;
              }
              this.selectedMetrics = checkedMetrics;
              this.renderSelectedRecord();
              this.drawChartForRecord();
              this.updateReturnToDefaultButton();
            });
          });
        }

    this.drawChartForRecord();
    this.updateReturnToDefaultButton();
  }

  private getNumericFields(record: any): Array<{ key: string; label: string }> {
  if (!record) return [];

  const excludeKeys = [
    'date',
    'symbol',
    'reportedCurrency',
    'fillingDate',
    'filingDate',
    'acceptedDate',
    'calendarYear',
    'fiscalYear',
    'period',
    'link',
    'finalLink',
    'cik',
    'weightedAverageShsOut',
    'weightedAverageShsOutDil',
    'eps',
    'epsDiluted'
  ];

  return Object.keys(record)
    .filter(key => {
      if (excludeKeys.includes(key)) return false;
      const value = record[key];
      return typeof value === 'number' && !Number.isNaN(value);
    })
    .map(key => ({
      key,
      label: key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, c => c.toUpperCase())
    }));
}


 private drawChartForRecord(): void {
  const echarts = (window as any).echarts;
  const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
  const instance = echarts.getInstanceByDom(container);
  if (instance) echarts.dispose(instance);

  if (!Array.isArray(this.currentData) || this.currentData.length === 0) {
    container.innerHTML = getNoDataHTML();
    return;
  }

  const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;

  // Use the metrics selected (max 4)
  const allMetrics = this.getNumericFields(this.currentData[0]);
  const selectedMetricKeys = this.selectedMetrics.length > 0 ? this.selectedMetrics : allMetrics.slice(0, 4).map(m => m.key);
  const activeMetrics = allMetrics.filter(m => selectedMetricKeys.includes(m.key)).slice(0, 4);

  // Prepare series: one series per metric across all records
  const seriesData = activeMetrics.map(metric => ({
    name: metric.label,
    type: 'bar',
    data: this.currentData.map((record:any) => this.toBillions(record[metric.key as keyof typeof record])),
    barWidth: '20%',
    itemStyle: { borderRadius: 0 }
  }));

  // X-axis: use filingDate (or date) for each record
  const xAxisData = this.currentData.map(r => {
    const dateStr = r.filingDate || r.fillingDate || r.date || '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr);
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getUTCFullYear();
    return `${month} ${year}`;
  });

  const textColor = getComputedStyle(widget).getPropertyValue('--charts-text-color');
  const splitLineColor = getComputedStyle(widget).getPropertyValue('--charts-split-line-color');
  const fontSize = getComputedStyle(widget).getPropertyValue('--font-size-sm');
  const bgColor = getComputedStyle(widget).getPropertyValue('--base-dark');

  const option = {
    backgroundColor: 'transparent',
    animation: false,
    grid: { left: 0, right: 0, top: 10, bottom: 0, containLabel: true },
    xAxis: { type: 'category', data: xAxisData, axisLabel: { color: textColor, fontSize: fontSize, margin: 16 } },
    yAxis: {
      type: 'value',
      axisLabel: { color: textColor, fontSize: fontSize, formatter: (v: number) => `$${v}B` },
      splitLine: { show: true, lineStyle: { color: splitLineColor, type: 'dashed' } }
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: bgColor,
      borderColor: bgColor,
      textStyle: {
          color: textColor,
          fontSize: fontSize
      },
      formatter: (params: any) => {
        // Show metric and value
        return params.map((p: any) => `${p.seriesName}: ${this.formatCurrency(p.data * 1e9)}`).join('<br/>');
      }
    },
    series: seriesData
  };

  const chart = echarts.init(container, null, { backgroundColor: getComputedStyle(widget).getPropertyValue('--bg-primary').trim() });
  chart.setOption(option);
  this.currentChart = chart;
}



  private populateRecordDropdown(): void {
    const dropdown = this.shadowRoot.querySelector('.record-dropdown') as HTMLSelectElement;
    if (!dropdown) return;
    if (!Array.isArray(this.currentData) || this.currentData.length === 0) {
      dropdown.innerHTML = '<option value="">No periods available</option>';
      return;
    }

    dropdown.innerHTML = this.currentData.map((item: any, idx: number) => {
      const label = this.getRecordLabel(item);
      return `<option value="${idx}" ${idx === this.selectedRecordIndex ? 'selected' : ''}>${label}</option>`;
    }).join('');
    dropdown.value = String(this.selectedRecordIndex);
  }

  private getRecordLabel(item: any): string {
    if (!item) return 'Unknown period';
    const date = item.date || item.filingDate || item.acceptedDate;
    if (!date) return 'Unknown period';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return String(date);
    const year = d.getUTCFullYear();
    const month = d.toLocaleString('default', { month: 'short' });
    return `${month} ${year}`;
  }

  private pickDefaultMetricKeys(allMetrics: Array<{ key: string; label: string }>): string[] {
    const fromDefs = this.getMetricDefinitions()
      .map(d => d.key)
      .filter(k => allMetrics.some(m => m.key === k));
    if (fromDefs.length > 0) return fromDefs.slice(0, 4);
    return allMetrics.slice(0, 4).map(m => m.key);
  }

  private getMetricDefinitions(): Array<{ key: string; label: string }> {
    if (this.selectedStatementType === 'BalanceSheet' || this.selectedStatementType === 'BalanceSheetStatmentsTtm') {
      return [
        { key: 'totalAssets', label: 'Total Assets' },
        { key: 'totalCurrentAssets', label: 'Current Assets' },
        { key: 'totalNonCurrentAssets', label: 'Non-Current Assets' },
        { key: 'totalLiabilities', label: 'Total Liabilities' },
        { key: 'totalEquity', label: 'Total Equity' }
      ];
    }
    if (this.selectedStatementType === 'CashflowStatement' || this.selectedStatementType === 'CashflowStatementsTtm') {
      return [
        { key: 'operatingCashFlow', label: 'Operating Cash Flow' },
        { key: 'capitalExpenditure', label: 'Capital Expenditure' },
        { key: 'freeCashFlow', label: 'Free Cash Flow' },
        { key: 'netCashProvidedByInvestingActivities', label: 'Investing Cash Flow' },
        { key: 'netCashProvidedByFinancingActivities', label: 'Financing Cash Flow' }
      ];
    }
    return [
      { key: 'revenue', label: 'Revenue' },
      { key: 'grossProfit', label: 'Gross Profit' },
      { key: 'operatingIncome', label: 'Operating Income' },
      { key: 'netIncome', label: 'Net Income' },
      { key: 'ebitda', label: 'EBITDA' }
    ];
  }

  private toBillions(value: any): number {
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    return num / 1e9;
  }

  private formatCurrency(value: any): string {
    const num = Number(value);
    if (Number.isNaN(num)) return 'N/A';
    const abs = Math.abs(num);
    if (abs >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseFinancialFundamental: typeof TraderverseFinancialFundamental; } }
if (typeof window !== 'undefined') { window.TraderverseFinancialFundamental = TraderverseFinancialFundamental; }
